import { Box, Button, ButtonGroup, Text, Modal as VHSModal, useTheme } from '@wistia/vhs';
import type { ReactElement, JSX } from 'react';
import { cloneElement, useContext, useEffect } from 'react';
import styled from 'styled-components';
import type { ReadonlyTuple } from 'type-fest';
import type { Trackable } from '~/onboarding/types';
import { useSnowplow } from '~/onboarding/hooks/useSnowplow/useSnowplow';
import { useLogger } from '../../../hooks/useLogger';
import { withErrorBoundary, useErrorBoundary } from '../../../hooks/useErrorBoundary';
import { OnboardingContext } from '../../../hooks/useOnboardingContext/OnboardingContext';
import { ModalCloseButton } from '../shared/ModalCloseButton';
import { StyledModalFooter } from '../shared/ModalFooter';
import type { SharedModalProps } from '../types';
import type { ValuePropCard, ValuePropCardProps } from './ValuePropCard';

const Content = styled.div`
  margin: ${({ theme }) => theme.spacing.space06};
`;

const Header = styled(Text)`
  margin: auto;
  margin-bottom: ${({ theme }) => theme.spacing.space04};
  max-width: 75%;
`;

type ValuePropCardComponentType = ReactElement<ValuePropCardProps, typeof ValuePropCard>;
export type ValuePropsModalProps = Pick<SharedModalProps, 'isOpen' | 'onRequestClose'> &
  Trackable & {
    /**
     * Text for the header
     */
    header: string;

    /**
     * Value props to display. An array length 2-3 of `<ValuePropCard>` components
     */
    valueProps:
      | ReadonlyTuple<ValuePropCardComponentType, 2>
      | ReadonlyTuple<ValuePropCardComponentType, 3>;

    /**
     * Label for the CTA button
     */
    ctaLabel: string;

    /**
     * When the CTA button is clicked, this function will be called
     */
    onClickCta: () => void;
  };

export const ValuePropsModalComponent = ({
  isOpen,
  onRequestClose,
  header,
  valueProps,
  ctaLabel,
  onClickCta,
  snowplowParams,
}: ValuePropsModalProps): JSX.Element => {
  const context = useContext(OnboardingContext);
  const logger = useLogger(context?.getCurrentFeatureId());
  useErrorBoundary((error: Error) => logger.error(error));

  const { spacing } = useTheme();
  const { logEvent } = useSnowplow();

  useEffect(() => {
    if (isOpen) {
      logEvent('valuePropsModalView', snowplowParams);
    }
  }, [isOpen, logEvent, snowplowParams]);

  return (
    <VHSModal
      isOpen={isOpen}
      onRequestClose={() => {
        logEvent('valuePropsModalDismiss', snowplowParams);
        onRequestClose();
      }}
      width="700px"
    >
      <ModalCloseButton />
      <Content>
        <Header
          align="center"
          variant="headline1"
        >
          {header}
        </Header>
        <Box gap={spacing.space05}>
          {valueProps.map((vP) => cloneElement(vP, { numOfCards: valueProps.length }))}
        </Box>
      </Content>
      <StyledModalFooter>
        <ButtonGroup align="center">
          <Button
            label={ctaLabel}
            onClick={() => {
              logEvent('valuePropsModalClick', snowplowParams);
              onClickCta();
            }}
          />
        </ButtonGroup>
      </StyledModalFooter>
    </VHSModal>
  );
};

export const ValuePropsModal = withErrorBoundary(ValuePropsModalComponent);
